// Import-free so node --test can load it: MediaOverlay sizes its card with this.

export interface BoxSize {
  width: number;
  height: number;
}

/** Height of the title bar stacked above the media, so it never covers the player's own chrome. */
export const MEDIA_BAR_HEIGHT = 48;
export const VIDEO_ASPECT = 16 / 9;

/**
 * Largest card whose media area keeps `aspect` (width / height) inside `bounds`, with `bar` px of
 * title bar stacked above the media. Whole pixels, rounded down so the card never overflows.
 */
export function fitMediaCard(bounds: BoxSize, aspect: number, bar: number = MEDIA_BAR_HEIGHT): BoxSize {
  const mediaHeight = bounds.height - bar;
  if (!(bounds.width > 0) || !(mediaHeight > 0) || !(aspect > 0)) return { width: 0, height: 0 };
  const width = Math.min(bounds.width, mediaHeight * aspect);
  return { width: Math.floor(width), height: Math.floor(width / aspect) + bar };
}

/** Scale that fits a page of `natural` size wholly inside `bounds`; 0 until both are measured. */
export function fitScale(bounds: BoxSize, natural: BoxSize): number {
  if (!(bounds.width > 0) || !(bounds.height > 0) || !(natural.width > 0) || !(natural.height > 0)) return 0;
  return Math.min(bounds.width / natural.width, bounds.height / natural.height);
}

/** A moved or resized card, in px relative to the free canvas area. */
export interface MediaFrame {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Smallest media width a corner resize may reach - still a tappable player on a wall panel. */
export const MIN_MEDIA_WIDTH = 240;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

/**
 * Keeps the whole card inside `bounds`, shrinking it first when the area got smaller than the card.
 * With an `aspect` the shrink keeps the media ratio under the `bar`.
 */
export function clampFrame(frame: MediaFrame, bounds: BoxSize, aspect: number = 0, bar: number = MEDIA_BAR_HEIGHT): MediaFrame {
  let width = Math.min(frame.width, Math.max(0, bounds.width));
  let height = Math.min(frame.height, Math.max(0, bounds.height));
  if (aspect > 0 && (width < frame.width || height < frame.height)) {
    width = Math.floor(Math.max(0, Math.min(width, (bounds.height - bar) * aspect)));
    height = Math.floor(width / aspect) + bar;
  }
  return { x: clamp(frame.x, 0, bounds.width - width), y: clamp(frame.y, 0, bounds.height - height), width, height };
}

/** The card moved by (dx, dy) from where the drag started, never leaving `bounds`. */
export function dragFrame(start: MediaFrame, dx: number, dy: number, bounds: BoxSize): MediaFrame {
  return clampFrame({ x: start.x + dx, y: start.y + dy, width: start.width, height: start.height }, bounds);
}

/**
 * Bottom-right corner resize from `start`. With an `aspect` the media keeps it (the bar is stacked
 * on top), following whichever axis the finger moved further; without one both axes are free.
 * The top-left corner stays put and the card never grows past `bounds`.
 */
export function resizeFrame(start: MediaFrame, dx: number, dy: number, bounds: BoxSize, aspect: number, bar: number = MEDIA_BAR_HEIGHT): MediaFrame {
  const roomWidth = bounds.width - start.x;
  const roomHeight = bounds.height - start.y;
  if (aspect > 0) {
    const byWidth = start.width + dx;
    const byHeight = (start.height - bar + dy) * aspect;
    const wanted = Math.abs(dx) >= Math.abs(dy * aspect) ? byWidth : byHeight;
    const maxWidth = Math.min(roomWidth, (roomHeight - bar) * aspect);
    const width = Math.floor(clamp(wanted, Math.min(MIN_MEDIA_WIDTH, maxWidth), maxWidth));
    return { x: start.x, y: start.y, width, height: Math.floor(width / aspect) + bar };
  }
  const width = Math.floor(clamp(start.width + dx, Math.min(MIN_MEDIA_WIDTH, roomWidth), roomWidth));
  const height = Math.floor(clamp(start.height + dy, Math.min(bar + 80, roomHeight), roomHeight));
  return { x: start.x, y: start.y, width, height };
}
